import { useState, useEffect } from 'react';
import { Copy } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Textarea } from '@/components/ui/Input';
import { copyToClipboard } from '@/lib/utils';
import { toast } from 'sonner';

const ALGOS = ['SHA-1', 'SHA-256', 'SHA-384', 'SHA-512'] as const;

async function hash(algo: (typeof ALGOS)[number], text: string): Promise<string> {
  if (!text) return '';
  const buf = new TextEncoder().encode(text);
  const out = await crypto.subtle.digest(algo, buf);
  return Array.from(new Uint8Array(out))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function HashGenerator() {
  const [input, setInput] = useState('Hello, Utility OS');
  const [hashes, setHashes] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    async function run() {
      const out: Record<string, string> = {};
      for (const a of ALGOS) {
        out[a] = await hash(a, input);
      }
      if (!cancelled) setHashes(out);
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [input]);

  return (
    <div>
      <ToolHeader
        toolId="hash-generator"
        title="Hash generator"
        description="Compute SHA-1, SHA-256, SHA-384, and SHA-512 digests of any text. Powered by Web Crypto."
      />

      <Card className="mb-4 !p-0">
        <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
          Input
        </div>
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="!border-0 !rounded-t-none min-h-[140px] !bg-transparent focus-visible:!ring-0"
          placeholder="Type or paste text to hash…"
        />
      </Card>

      <div className="space-y-2">
        {ALGOS.map((a) => (
          <Card key={a} className="!p-3">
            <div className="flex items-center gap-3">
              <div className="w-20 shrink-0 text-xs font-mono font-semibold uppercase tracking-wider text-fg-muted">
                {a}
              </div>
              <div className="flex-1 font-mono text-[13px] break-all min-w-0">
                {hashes[a] || '…'}
              </div>
              <button
                onClick={async () => {
                  if (!hashes[a]) return;
                  const ok = await copyToClipboard(hashes[a]);
                  if (ok) toast.success(`${a} copied`);
                }}
                className="shrink-0 size-7 rounded-md hover:bg-surface-hover flex items-center justify-center text-fg-subtle hover:text-fg"
              >
                <Copy size={12} />
              </button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default HashGenerator;
